import { create, ApisauceConfig } from "apisauce"
import { _t } from "@cruisen/i18n"
import { isFunctionOutput } from "./callFirebaseFunction"
import {
  GetCruiseDetailsInput,
  GetCruiseDetailsOutput,
  RevealCreditCardInput,
  RevealCreditCardOutput,
  SaveUserCreditCardOutput,
  SaveUserCreditCardSecuredInput,
  SaveProfileInput,
  SaveProfileOutput,
  RevealPassportNumberInput,
  RevealPassportNumberOutput,
  GetCruiseListInput,
  GetCruiseListOutput,
  FunctionOutput,
} from "@cruisen/contract"
import { auth } from "./firebase.ts"
import Config from "../config"

export const getFunctionUrl = (functionName: string): string => {
  const location = "us-central1"
  const projectId = Config.firebaseProjectId

  if (Config.useEmulators) {
    return `http://127.0.0.1:5001/${projectId}/${location}/${functionName}`
  }

  return `https://${location}-${projectId}.cloudfunctions.net/${functionName}`
}

type FirebaseHttpFunctions = {
  billingRevealCreditCard: {
    payload: RevealCreditCardInput
    result: RevealCreditCardOutput
  }
  billingSaveCreditCard: {
    payload: SaveUserCreditCardSecuredInput
    result: SaveUserCreditCardOutput
  }
  publicGetCruiseDetails: {
    payload: GetCruiseDetailsInput
    result: GetCruiseDetailsOutput
  }
  publicGetCruiseList: {
    payload: GetCruiseListInput
    result: GetCruiseListOutput
  }
  publicGetTrip: {
    payload: { id: string }
    result: FunctionOutput
  }
  saveProfile: {
    payload: SaveProfileInput
    result: SaveProfileOutput
  }
  revealPassportNumber: {
    payload: RevealPassportNumberInput
    result: RevealPassportNumberOutput
  }
}

type FirebaseHttpFunctionResult<T extends keyof FirebaseHttpFunctions> =
  | { error: false; data: FirebaseHttpFunctions[T]["result"] }
  | { error: true; data: null; message: string }

export const callFirebaseHttpFunction = async <T extends keyof FirebaseHttpFunctions>({
  name,
  payload,
}: {
  name: T
  payload: FirebaseHttpFunctions[T]["payload"]
}): Promise<FirebaseHttpFunctionResult<T>> => {
  console.log(`[${name}] call firebase function over http`)
  const functionUrl = getFunctionUrl(name)

  try {
    let firebaseToken = null
    if (auth.currentUser) {
      firebaseToken = await auth.currentUser.getIdToken()
    }
    const secureProxy = Config.firebaseFunctionSecureProxies?.[name]

    let requestConfig: ApisauceConfig
    if (secureProxy) {
      requestConfig = {
        baseURL: secureProxy.proxyUrl,
        headers: {
          Authorization: `Bearer ${firebaseToken}`,
          ...secureProxy.headers,
        },
      }
    } else {
      requestConfig = {
        baseURL: functionUrl,
        headers: {
          Authorization: `Bearer ${firebaseToken}`,
        },
      }
    }

    const api = create(requestConfig)

    const response = await api.post("", payload)
    const data = response.data as FirebaseHttpFunctions[T]["result"]

    if (isFunctionOutput(data) && !data.success) {
      console.error(`[${name}] firebase http function error: `, {
        error: new Error(
          `[${name}] firebase function call over http failure: ${JSON.stringify({
            message: data.message,
            code: data.code,
            details: data.details,
          })}`,
        ),
        functionUrl,
      })

      return {
        error: true,
        data: null,
        message: data.message ?? _t("errorScreen.title"),
      }
    }

    return {
      error: false,
      data,
    }
  } catch (error) {
    console.error(`[${name}] firebase http function error: `, {
      error,
      functionUrl,
    })

    return {
      error: true,
      data: null,
      message: error.toString(),
    }
  }
}
